import { createContext, useContext, useEffect, useRef } from 'react'
import { amplifyOutputs } from '@/amplify-config'

export type RealtimeEvent = { kind: string; payload?: Record<string, unknown> }
type Listener = (event: RealtimeEvent) => void

type RealtimeAPI = {
  subscribe: (listener: Listener) => () => void
}

const Ctx = createContext<RealtimeAPI | null>(null)

/** Find the WebSocket URL CDK wrote into amplify_outputs.json. */
function findWsUrl(): string {
  const o = amplifyOutputs as Record<string, unknown>
  const c = (o.custom as Record<string, unknown>) ?? {}
  for (const v of Object.values(c)) {
    if (v && typeof v === 'object' && 'WebSocketUrl' in v) {
      const url = (v as Record<string, unknown>)['WebSocketUrl']
      if (typeof url === 'string') return url
    }
  }
  return ''
}

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const listenersRef = useRef<Set<Listener>>(new Set())

  useEffect(() => {
    const wsUrl = findWsUrl()
    if (!wsUrl) return

    let ws: WebSocket | null = null
    let reconnectTimer: number | null = null
    let closed = false
    // Consecutive socket failures. Backoff applies to real failures only; the
    // no-token check below stays at a quick 3 s so sign-in connects promptly.
    let failures = 0

    async function connect() {
      if (closed) return
      // WS $connect authorizer needs the Cognito access token as ?token=
      // (browsers can't set WS headers); without it the socket is denied.
      let token: string | undefined
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth')
        token = (await fetchAuthSession()).tokens?.accessToken?.toString()
      } catch { /* not signed in yet */ }
      if (closed) return
      // No token (e.g. on /login): don't open a socket the authorizer will
      // refuse — that logged "HTTP Authentication failed" every 3 s (QA
      // 2026-10-05). Check again shortly; the first check after sign-in connects.
      if (!token) {
        scheduleReconnect(3000)
        return
      }
      const url = `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
      try {
        ws = new WebSocket(url)
      } catch {
        scheduleReconnect(nextBackoff())
        return
      }
      ws.onopen = () => { failures = 0 }
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(String(e.data)) as RealtimeEvent
          if (data && typeof data.kind === 'string') {
            listenersRef.current.forEach((l) => l(data))
          }
        } catch {
          /* ignore non-JSON frames */
        }
      }
      ws.onclose = () => {
        if (!closed) scheduleReconnect(nextBackoff())
      }
      ws.onerror = () => {
        // onclose will fire after onerror; let it handle the reconnect
      }
    }

    // 3 s, 6 s, 12 s … capped at 60 s, with ±25% jitter so many tabs don't retry in
    // lockstep. A refused or dropped socket used to retry every 3 s forever.
    function nextBackoff() {
      const base = Math.min(60_000, 3000 * 2 ** failures)
      failures += 1
      return Math.round(base * (0.75 + Math.random() * 0.5))
    }

    function scheduleReconnect(delayMs: number) {
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      reconnectTimer = window.setTimeout(() => { void connect() }, delayMs)
    }

    void connect()
    return () => {
      closed = true
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      ws?.close()
    }
  }, [])

  const api: RealtimeAPI = {
    subscribe(listener) {
      listenersRef.current.add(listener)
      return () => {
        listenersRef.current.delete(listener)
      }
    },
  }
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}

/**
 * Subscribe to realtime events. Returns an unsubscribe function.
 * Pass a `kindPrefix` (e.g. "deal." or "rfp.") to filter, or omit for all.
 */
export function useRealtime(listener: Listener, kindPrefix?: string): void {
  const ctx = useContext(Ctx)
  useEffect(() => {
    if (!ctx) return
    return ctx.subscribe((event) => {
      if (!kindPrefix || event.kind.startsWith(kindPrefix)) listener(event)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, kindPrefix])
}
